/**
 * The Briefing — Duty's grounded situational read. A short advisory paragraph
 * built deterministically from the structured incident context, distinct from
 * the labelled SITREP lines. This is both the always-available baseline (works
 * with `AI_ENABLED=false`) and the exact template the LLM may *rephrase* under
 * the grounding contract — it must never add facts, numbers, or doctrine.
 *
 * Copy stays advisory ("Worth considering…"), never commanding, and preserves
 * any stated uncertainty.
 */

import type { Incident } from './incident.js';
import { CONFIRMATION_LABELS, STRATEGY_LABELS } from './incident.js';
import type { Insight, InsightSeverity } from './insights/index.js';

const SEV_ORDER: Record<InsightSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  info: 3,
};

const sentence = (s: string): string => {
  const t = s.trim().replace(/\s+/g, ' ');
  if (!t) return '';
  const cap = t[0]!.toUpperCase() + t.slice(1);
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
};

/** The advisory disclaimer that closes every briefing. */
export const BRIEFING_DISCLAIMER = 'Advisory only — confirm all facts before acting.';

/**
 * Build the deterministic situational briefing. Pure: reads only structured
 * fields already in the incident + the live insights (which are themselves
 * deterministic). Returns one short advisory paragraph.
 */
export function buildBriefing(incident: Incident, insights: readonly Insight[] = []): string {
  const inc = incident.itasc.incident;
  const parts: string[] = [];

  // 1. Situational lead — confirmation status, type and location.
  const typeWord =
    inc.incidentType || incident.typeConfirmed || incident.typeReported || 'incident';
  const lead = `${CONFIRMATION_LABELS[inc.confirmation]} ${typeWord}`.replace(/\s+/g, ' ').trim();
  const loc = inc.locationConfirmed
    ? `at ${incident.location}; location confirmed`
    : `at ${incident.location} (location to confirm)`;
  parts.push(sentence(`${lead} ${loc}`));

  // 2. Life read, if captured (first-class — leads the threats).
  const life = incident.itasc.threats.life;
  if (life) parts.push(sentence(`Life: ${life}`));

  // 3. Current strategy, if chosen.
  const strat = incident.itasc.actions.strategy;
  if (strat) parts.push(sentence(`Strategy: ${STRATEGY_LABELS[strat]}`));

  // 4. The next things worth considering — the top open insights by severity.
  const open = [...insights]
    .filter((i) => i.status === 'open' || i.status === 'acknowledged')
    .sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);
  if (open.length > 0) {
    const items = open.slice(0, 2).map((i) => `${i.title} (${i.severity})`).join('; ');
    const more = open.length > 2 ? ` (+${open.length - 2} more)` : '';
    parts.push(sentence(`Worth considering: ${items}${more}`));
  } else {
    parts.push('No outstanding advisories at this time.');
  }

  parts.push(BRIEFING_DISCLAIMER);
  return parts.join(' ');
}

/**
 * Grounding-contract system prompt for the Briefing: the model may ONLY rephrase
 * the supplied briefing into a calm spoken-style read — no new facts, numbers,
 * doctrine or recommendations, advisory language preserved.
 */
export function briefingSystemPrompt(): string {
  return [
    'You are Duty, an advisory assistant for NSW emergency-services officers.',
    'Rephrase the provided situational briefing into one short, calm, spoken-style paragraph.',
    'Use ONLY the facts in the provided briefing — do not add incidents, numbers, doctrine, or recommendations.',
    'Keep advisory language ("Consider…", "Appears to…", "Worth confirming…"); never issue commands.',
    'Do not invent certainty; preserve any stated uncertainty. Be concise (2–4 sentences).',
  ].join(' ');
}

/** Build the user prompt: the deterministic briefing the model must rephrase. */
export function briefingUserPrompt(deterministic: string): string {
  return `Situational briefing (rephrase only, add nothing):\n${deterministic}`;
}
